import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import React from 'react';

type AlertVariant = 'info' | 'warning' | 'amber' | 'error' | 'success';

const VARIANT_STYLES: Record<
    AlertVariant,
    {
        container: string;
        icon: string;
        body: string;
        Icon: React.ElementType;
    }
> = {
    info: {
        container:
            'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30',
        icon: 'text-blue-600 dark:text-blue-400',
        body: 'text-blue-800 dark:text-blue-300',
        Icon: Info,
    },
    warning: {
        container:
            'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/30',
        icon: 'text-yellow-600 dark:text-yellow-400',
        body: 'text-yellow-800 dark:text-yellow-300',
        Icon: Info,
    },
    amber: {
        container:
            'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30',
        icon: 'text-amber-600 dark:text-amber-400',
        body: 'text-amber-800 dark:text-amber-300',
        Icon: AlertTriangle,
    },
    error: {
        container:
            'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30',
        icon: 'text-red-600 dark:text-red-400',
        body: 'text-red-700 dark:text-red-300',
        Icon: AlertCircle,
    },
    success: {
        container:
            'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30',
        icon: 'text-green-600 dark:text-green-400',
        body: 'text-green-800 dark:text-green-300',
        Icon: CheckCircle2,
    },
};

interface AlertBannerProps {
    variant?: AlertVariant;
    title?: string;
    children: React.ReactNode;
    className?: string;
}

const AlertBanner: React.FC<AlertBannerProps> = ({
    variant = 'info',
    title,
    children,
    className = '',
}) => {
    const styles = VARIANT_STYLES[variant];
    const { Icon } = styles;

    return (
        <div
            className={`rounded-lg border p-4 ${styles.container} ${className}`}
        >
            <div className="flex items-start gap-3">
                <Icon size={20} className={`mt-0.5 shrink-0 ${styles.icon}`} />
                <div className={`text-sm ${styles.body}`}>
                    {title && <p className="mb-1 font-semibold">{title}</p>}
                    {children}
                </div>
            </div>
        </div>
    );
};

export default AlertBanner;
